<template>
  <main>
    <h3 class="d-none d-md-block">CIERRES DE CAJA</h3>
    <h3 class="d-md-none font-weight-bold">CIERRES <br />DE CAJA</h3>
    
    <!-- Descripción del módulo -->
    <div class="alert d-flex align-items-center mb-4" role="alert" style="background-color: #66C4DE; border-color: #66C4DE;">
      <i class="fas fa-info-circle fa-2x" style="margin-right: 15px; color: #fff;"></i>
      <div>
        <p class="mb-0" style="color: #353535;">
          <strong>Supervisa y audita todos los cierres de caja realizados en tus sucursales.</strong> 
          Este módulo centraliza la información de los arqueos diarios, permitiéndote verificar diferencias 
          entre el sistema y lo ratificado por los recepcionistas, identificar patrones de descuadre, 
          exportar reportes detallados para control interno, y descargar el respaldo completo de cada 
          cierre para mantener un registro histórico confiable de las operaciones de caja.
        </p>
      </div>
    </div>
    
    <!-- Tabla de cierres de caja -->
    <cash-closure-table
      ref="cashClosureTable"
      :cashiers="cashiers"
      @search="handleSearch"
      @searchUnified="handleSearchFromTable"
      @fetchNewPage="handleFetchNewPage"
      @changeItemsPerPage="handleChangeItemsPerPage"
      @export="handleExportRecords"
      @showDetail="handleShowDetail"
      @downloadExcel="handleDownloadExcel"
      @printClosure="handlePrintClosure"
      :records="records"
      :loading="loading"
      :totalRecords="totalRecords"
      :allFilteredRecords="allFilteredRecords"
      :originalFilteredRecords="originalFilteredRecords"
      :localPagination="localPagination"
      :metrics="metrics"
    />
  </main>
</template>

<script>
import CashClosureTable from "./CashClosing/CashClosureTable.vue";
import { CentralGymOPS_API } from "@/axiosService";

export default {
  name: "GymSalesCashClosingDashboard",
  components: {
    CashClosureTable
  },
  
  data() {
    return {
      loading: false,
      cashiers: [],
      records: [],
      totalRecords: 0,
      allFilteredRecords: [],
      originalFilteredRecords: [],
      localPagination: {
        currentPage: 1,
        itemsPerPage: 20,
        totalPages: 1
      },
      metrics: {},
      lastSearchFilters: null // Para guardar los últimos filtros aplicados
    };
  },
  
  methods: {
    async initializeComponent() {
      await this.fetchCashiers();
      
      await this.handleSearchFromTable({
        dateFrom: this.$moment().startOf('month').format('YYYY-MM-DD'),
        dateTo: this.$moment().endOf('month').format('YYYY-MM-DD')
      });
    },
    
    async fetchCashiers() {
      this.cashiers = [
        { value: 'Caja 1', label: 'Caja 1' },
        { value: 'Caja 2', label: 'Caja 2' },
        { value: 'Caja 3', label: 'Caja 3' },
        { value: 'Caja 4', label: 'Caja 4' }
      ];
    },
    
    async handleSearchFromTable(filters) {
      // Guardar los filtros para usar en paginación
      this.lastSearchFilters = filters;
      
      this.loading = true;
      try {
        // Mapear filtros de UI -> API (YYYY-MM-DD)
        const filtrosBase = {
          fechaInicio: filters.dateFrom ? this.$moment(filters.dateFrom).format('YYYY-MM-DD') : undefined,
          fechaFin: filters.dateTo ? this.$moment(filters.dateTo).format('YYYY-MM-DD') : undefined,
          sucursalId: filters.branch || undefined,

          caja: filters.cashier || undefined,
          searchTerm: filters.searchTerm || filters.searchText || undefined,

        };

        Object.keys(filtrosBase).forEach(key => filtrosBase[key] === undefined && delete filtrosBase[key]);

        const token = sessionStorage.getItem('jCxbraoQeD') || '';

        const requestPaginado = {
          Header: { Operacion: '/v1/cierres-caja/consultar-paginados' },
          Auth: { Token: token },
          Parameters: {
            ...filtrosBase,
            page: 1,
            limit: this.localPagination.itemsPerPage
          },
          Body: {}
        };

        const requestMetricas = {
          Header: { Operacion: '/v1/cierres-caja/metrics' },
          Auth: { Token: token },
          Parameters: { ...filtrosBase },
          Body: {}
        };

        const [closuresResponse, metricsResponse] = await Promise.all([
          CentralGymOPS_API.post('', requestPaginado),
          CentralGymOPS_API.post('', requestMetricas)
        ]);

        // Extraer datos de forma tolerante a distintas envolturas
        const pagData = (closuresResponse.data && (closuresResponse.data.Response?.Data || closuresResponse.data.data || closuresResponse.data.Data)) || {};
        const metData = (metricsResponse.data && (metricsResponse.data.Response?.Data || metricsResponse.data.data || metricsResponse.data.Data)) || {};

        const cierres = pagData.cierresCaja || pagData.records || [];
        const paginacion = pagData.paginacion || {};

        this.records = Array.isArray(cierres) ? cierres : [];
        this.totalRecords = paginacion.totalRegistros || pagData.totalRecords || this.records.length;
        this.allFilteredRecords = this.records;
        this.originalFilteredRecords = this.records;
        this.localPagination.totalPages = paginacion.totalPaginas || Math.max(1, Math.ceil(this.totalRecords / this.localPagination.itemsPerPage));
        this.localPagination.currentPage = 1;

        // Métricas
        this.metrics = metData.metricas || metData || {};
        
      } catch (error) {
        console.error("Error al buscar cierres:", error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al buscar cierres de caja',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading = false;
      }
    },
    
    async handleFetchNewPage(page) {
      // Guardar los filtros actuales
      const lastFilters = this.lastSearchFilters || {
        dateFrom: this.$moment().startOf('month').format('YYYY-MM-DD'),
        dateTo: this.$moment().endOf('month').format('YYYY-MM-DD')
      };
      
      this.loading = true;
      try {
        // Mapear filtros de UI -> API
        const filtrosBase = {
          fechaInicio: lastFilters.dateFrom ? this.$moment(lastFilters.dateFrom).format('YYYY-MM-DD') : undefined,
          fechaFin: lastFilters.dateTo ? this.$moment(lastFilters.dateTo).format('YYYY-MM-DD') : undefined,
          sucursalId: lastFilters.branch || undefined,
          caja: lastFilters.cashier || undefined,
          searchTerm: lastFilters.searchTerm || lastFilters.searchText || undefined,

        };

        Object.keys(filtrosBase).forEach(key => filtrosBase[key] === undefined && delete filtrosBase[key]);

        const token = sessionStorage.getItem('jCxbraoQeD') || '';

        const requestPaginado = {
          Header: { Operacion: '/v1/cierres-caja/consultar-paginados' },
          Auth: { Token: token },
          Parameters: {
            ...filtrosBase,
            page: page,
            limit: this.localPagination.itemsPerPage
          },
          Body: {}
        };

        const response = await CentralGymOPS_API.post('', requestPaginado);

        // Extraer datos
        const pagData = (response.data && (response.data.Response?.Data || response.data.data || response.data.Data)) || {};
        const cierres = pagData.cierresCaja || pagData.records || [];
        const paginacion = pagData.paginacion || {};

        this.records = Array.isArray(cierres) ? cierres : [];
        this.totalRecords = paginacion.totalRegistros || pagData.totalRecords || this.records.length;
        this.localPagination.currentPage = page;
        this.localPagination.totalPages = paginacion.totalPaginas || Math.ceil(this.totalRecords / this.localPagination.itemsPerPage);
        
      } catch (error) {
        console.error("Error al cambiar página:", error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al cargar la página solicitada',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading = false;
      }
    },
    
    async handleExportRecords(filters) {
      try {
        // Aplicar los mismos filtros que la búsqueda para obtener todos los registros
        const filtrosBase = {
          fechaInicio: filters.dateFrom ? this.$moment(filters.dateFrom).format('YYYY-MM-DD') : undefined,
          fechaFin: filters.dateTo ? this.$moment(filters.dateTo).format('YYYY-MM-DD') : undefined,
          sucursalId: filters.branch || undefined,

          caja: filters.cashier || undefined,
          searchTerm: filters.searchTerm || filters.searchText || undefined,

        };

        Object.keys(filtrosBase).forEach(key => filtrosBase[key] === undefined && delete filtrosBase[key]);

        const token = sessionStorage.getItem('jCxbraoQeD') || '';
        const request = {
          Header: { Operacion: '/v1/cierres-caja/consultar-paginados' },
          Auth: { Token: token },
          Parameters: {
            ...filtrosBase,
            page: 1,
            limit: 1000 // Obtener todos los registros para exportar
          },
          Body: {}
        };

        const response = await CentralGymOPS_API.post('', request);
        const respData = (response.data && (response.data.Response?.Data || response.data.data || response.data.Data)) || {};
        const cierres = respData.cierresCaja || respData.records || [];

        if (cierres.length > 0) {
          // Preparar datos para Excel
          const excelData = cierres.map(cierre => ({
            'Caja': cierre.caja,
            'Responsable': cierre.responsable || 'Sin responsable',
            'Sucursal': cierre.nombreSucursal || 'Sin sucursal',
            'Fecha Apertura': this.formatDate(cierre.fechaApertura),
            'Hora Apertura': cierre.horaApertura,
            'Fecha Cierre': this.formatDate(cierre.fechaCierre),
            'Hora Cierre': cierre.horaCierre,
            'Total Sistema': (cierre.totalSistema && cierre.totalSistema.ventasTotales) || 0,
            'Total Ratificado': (cierre.totalRatificado && cierre.totalRatificado.ventasTotales) || 0,
            'Diferencia': (cierre.totalRatificado && cierre.totalRatificado.totalDiferencia) || 0,
            'Efectivo Sistema': (cierre.totalSistema && cierre.totalSistema.ventasEfectivo) || 0,
            'Efectivo Ratificado': (cierre.totalRatificado && cierre.totalRatificado.ventasEfectivo) || 0,
            'Débito Sistema': (cierre.totalSistema && cierre.totalSistema.ventasTDebito) || 0,
            'Débito Ratificado': (cierre.totalRatificado && cierre.totalRatificado.ventasTDebito) || 0,
            'Crédito Sistema': (cierre.totalSistema && cierre.totalSistema.ventasTCredito) || 0,
            'Crédito Ratificado': (cierre.totalRatificado && cierre.totalRatificado.ventasTCredito) || 0
          }));

          // Exportar como CSV compatible con Excel
          await this.exportToExcel(excelData, `Cierres_Caja_${this.$moment().format('YYYY-MM-DD')}.csv`);
          
          this.$swal.fire({
            icon: 'success',
            title: 'Exportación exitosa',
            text: 'El archivo Excel se ha descargado correctamente',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          this.$swal.fire({
            icon: 'warning',
            title: 'Sin datos',
            text: 'No hay registros para exportar con los filtros aplicados',
            confirmButtonColor: '#6bd098'
          });
        }
      } catch (error) {
        console.error("Error al exportar:", error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo exportar los datos',
          confirmButtonColor: '#6bd098'
        });
      }
    },
    
    handleShowDetail(record) {
      this.$swal.fire({
        title: 'Detalle del Cierre',
        html: `
          <div class="text-left">
            <p><strong>Responsable:</strong> ${record.responsable}</p>
            <p><strong>Caja:</strong> ${record.caja}</p>
            <p><strong>Sucursal:</strong> ${record.nombreSucursal}</p>
            <p><strong>Total Sistema:</strong> $${this.formatMoney((record.totalSistema && record.totalSistema.ventasTotales) || 0)}</p>
            <p><strong>Total Ratificado:</strong> $${this.formatMoney((record.totalRatificado && record.totalRatificado.ventasTotales) || 0)}</p>
            <p><strong>Diferencia:</strong> $${this.formatMoney(Math.abs((record.totalRatificado && record.totalRatificado.totalDiferencia) || 0))}</p>
          </div>
        `,
        confirmButtonColor: '#6bd098'
      });
    },
    
    async handleDownloadExcel(recordId) {
      try {
        const token = sessionStorage.getItem('jCxbraoQeD') || '';
        const request = {
          Header: { Operacion: '/v1/cierres-caja/descargar-archivo' },
          Auth: { Token: token },
          Parameters: { cierreId: recordId },
          Body: {}
        };

        const response = await CentralGymOPS_API.post('', request);

        const respData = (response.data && (response.data.Response?.Data || response.data.data || response.data.Data)) || {};

        // Si viene base64 según especificación
        if (respData.archivoBase64 && respData.nombreArchivo) {
          const byteCharacters = atob(respData.archivoBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', respData.nombreArchivo);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else if (response.data instanceof Blob) {
          // Fallback si el backend responde con blob directo
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Cierre_${recordId}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          throw new Error('Respuesta de descarga no reconocida');
        }
        
        // Descarga completada exitosamente
        console.log('✅ Excel descargado exitosamente');
      } catch (error) {
        console.error("Error al descargar Excel:", error);
      }
    },
    
    handlePrintClosure(record) {
      this.$swal.fire({
        title: 'Función de Impresión',
        text: 'La impresión del cierre se implementará próximamente',
        icon: 'info',
        confirmButtonColor: '#6bd098'
      });
    },
    
    formatDate(date) {
      if (!date) return '-';
      return this.$moment(date).format('DD-MM-YYYY');
    },
    
    formatMoney(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat('es-CL').format(value);
    },
    
    handleSearch(filters) {
      this.handleSearchFromTable(filters);
    },
    
    async handleChangeItemsPerPage(newItemsPerPage) {
      // Actualizar items por página
      this.localPagination.itemsPerPage = newItemsPerPage;
      // Volver a la página 1 con el nuevo tamaño
      await this.handleFetchNewPage(1);
    },

    // Utilidad local simple para exportar a CSV (compatible con Excel)
    exportToExcel(rows, filename) {
      if (!rows || rows.length === 0) {
        return Promise.resolve();
      }
      const headers = Object.keys(rows[0]);
      const escapeCell = (cell) => {
        if (cell === null || cell === undefined) return '';
        const str = String(cell).replace(/"/g, '""');
        return /[",\n]/.test(str) ? `"${str}"` : str;
      };
      const csv = [headers.join(',')]
        .concat(rows.map(r => headers.map(h => escapeCell(r[h])).join(',')))
        .join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return Promise.resolve();
    }
  },
  
  mounted() {
    this.initializeComponent();
  }
};
</script>

<style scoped>
main {
  padding: 20px;
}

h3 {
  color: #66615B;
  margin-bottom: 25px;
}
</style>